import type { Assistant } from '@/api/assistants/assistants.types';
import { defaultAssistantConfig } from '@/api/assistants/defaultConfig';

// Fields to exclude from export (sensitive or auto-generated)
const EXCLUDED_FIELDS = [
  'id',
  '_id', 
  'orgId',
  'organization_id',
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at',
  'isServerUrlSecretSet'
] as const;

// Configuration that should be exported
export interface ExportableAssistantConfig {
  name: string;
  model: Omit<Assistant['model'], 'toolIds' | 'knowledgeBaseId'>; // Exclude org-specific fields
  voice: Assistant['voice'];
  firstMessageMode: string;
  hipaaEnabled: boolean;
  clientMessages: string[];
  serverMessages: string[];
  silenceTimeoutSeconds: number;
  maxDurationSeconds: number;
  backgroundSound: string;
  backchannelingEnabled: boolean;
  backgroundDenoisingEnabled: boolean;
  modelOutputInMessagesEnabled: boolean;
  transportConfigurations: Assistant['transportConfigurations'];
  firstMessage: string;
  voicemailDetection: Assistant['voicemailDetection'];
  voicemailMessage: string;
  endCallMessage: string;
  endCallPhrases: string[];
  metadata: Record<string, string>;
  // Note: server field is completely excluded as it's not part of standard assistant creation API
  analysisPlan: Assistant['analysisPlan'];
  artifactPlan: Assistant['artifactPlan'];
  messagePlan: Assistant['messagePlan'];
  startSpeakingPlan: Assistant['startSpeakingPlan'];
  stopSpeakingPlan: Assistant['stopSpeakingPlan'];
  monitorPlan: Assistant['monitorPlan'];
  transcriber: Assistant['transcriber'];
  endCallFunctionEnabled?: boolean;
  dialKeypadFunctionEnabled?: boolean;
  forwardingPhoneNumber?: string | null;
}

/**
 * Export assistant configuration to a clean JSON object
 */
export const exportAssistantConfig = (assistant: Assistant): ExportableAssistantConfig => {
  // Create a clean copy without excluded fields
  const exportConfig: Record<string, unknown> = {};
  
  // Copy all allowed fields
  (Object.keys(assistant) as Array<keyof Assistant>).forEach(key => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!EXCLUDED_FIELDS.includes(key as any)) {
      exportConfig[key] = assistant[key];
    }
  });

  // Handle model config specially to exclude organization-specific fields
  if (assistant.model) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { toolIds, knowledgeBaseId, ...modelWithoutOrgSpecific } = assistant.model;
    exportConfig.model = modelWithoutOrgSpecific;
  }

  // Server config is completely excluded from export as it's not part of standard assistant creation
  // Server configurations are handled separately and should not be included in portable configs

  // Ensure we have all required fields with defaults and proper typing
  return {
    name: assistant.name,
    model: exportConfig.model as Omit<Assistant['model'], 'toolIds' | 'knowledgeBaseId'>,
    voice: exportConfig.voice as Assistant['voice'],
    firstMessageMode: exportConfig.firstMessageMode as string,
    hipaaEnabled: exportConfig.hipaaEnabled as boolean,
    clientMessages: exportConfig.clientMessages as string[],
    serverMessages: exportConfig.serverMessages as string[],
    silenceTimeoutSeconds: exportConfig.silenceTimeoutSeconds as number,
    maxDurationSeconds: exportConfig.maxDurationSeconds as number,
    backgroundSound: exportConfig.backgroundSound as string,
    backchannelingEnabled: exportConfig.backchannelingEnabled as boolean,
    backgroundDenoisingEnabled: exportConfig.backgroundDenoisingEnabled as boolean,
    modelOutputInMessagesEnabled: exportConfig.modelOutputInMessagesEnabled as boolean,
    transportConfigurations: exportConfig.transportConfigurations as Assistant['transportConfigurations'],
    firstMessage: exportConfig.firstMessage as string,
    voicemailDetection: exportConfig.voicemailDetection as Assistant['voicemailDetection'],
    voicemailMessage: exportConfig.voicemailMessage as string,
    endCallMessage: exportConfig.endCallMessage as string,
    endCallPhrases: exportConfig.endCallPhrases as string[],
    metadata: exportConfig.metadata as Record<string, string>,
    // server field excluded - not part of standard assistant creation API
    analysisPlan: exportConfig.analysisPlan as Assistant['analysisPlan'],
    artifactPlan: exportConfig.artifactPlan as Assistant['artifactPlan'],
    messagePlan: exportConfig.messagePlan as Assistant['messagePlan'],
    startSpeakingPlan: exportConfig.startSpeakingPlan as Assistant['startSpeakingPlan'],
    stopSpeakingPlan: exportConfig.stopSpeakingPlan as Assistant['stopSpeakingPlan'],
    monitorPlan: exportConfig.monitorPlan as Assistant['monitorPlan'],
    transcriber: exportConfig.transcriber as Assistant['transcriber'],
    endCallFunctionEnabled: exportConfig.endCallFunctionEnabled as boolean,
    dialKeypadFunctionEnabled: exportConfig.dialKeypadFunctionEnabled as boolean,
    forwardingPhoneNumber: exportConfig.forwardingPhoneNumber as string | null
  };
};

/**
 * Download assistant configuration as JSON file
 */
export const downloadAssistantConfig = (assistant: Assistant) => {
  const config = exportAssistantConfig(assistant);
  
  // Create filename with assistant name and timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  const sanitizedName = assistant.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const filename = `assistant_${sanitizedName}_${timestamp}.json`;
  
  // Create and download file
  const blob = new Blob([JSON.stringify(config, null, 2)], { 
    type: 'application/json' 
  });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  return filename;
};

/**
 * Validate imported configuration structure
 */
export const validateImportedConfig = (config: unknown): { 
  isValid: boolean; 
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if it's a valid object
  if (!config || typeof config !== 'object') {
    errors.push('Invalid configuration format. Expected a JSON object.');
    return { isValid: false, errors, warnings };
  }

  // Type guard to ensure config is an object
  const configObj = config as Record<string, unknown>;

  // Required fields validation
  if (!configObj.name || typeof configObj.name !== 'string') {
    errors.push('Assistant name is required and must be a string.');
  }

  if (configObj.name && typeof configObj.name === 'string' && configObj.name.length > 40) {
    errors.push('Assistant name must be 40 characters or less.');
  }

  // Model validation
  if (!configObj.model || typeof configObj.model !== 'object') {
    errors.push('Model configuration is required.');
  } else {
    const model = configObj.model as Record<string, unknown>;
    if (!Array.isArray(model.messages)) {
      errors.push('Model messages must be an array.');
    }
    if (typeof model.temperature !== 'number') {
      warnings.push('Model temperature should be a number. Using default value.');
    }
    if (typeof model.maxTokens !== 'number') {
      warnings.push('Model maxTokens should be a number. Using default value.');
    }
  }

  // Voice validation
  if (!configObj.voice || typeof configObj.voice !== 'object') {
    errors.push('Voice configuration is required.');
  } else {
    const voice = configObj.voice as Record<string, unknown>;
    if (!voice.voiceId || typeof voice.voiceId !== 'string') {
      errors.push('Voice ID is required and must be a string.');
    }
  }

  // Transcriber validation
  if (!configObj.transcriber || typeof configObj.transcriber !== 'object') {
    errors.push('Transcriber configuration is required.');
  } else {
    const transcriber = configObj.transcriber as Record<string, unknown>;
    if (!transcriber.language || typeof transcriber.language !== 'string') {
      errors.push('Transcriber language is required and must be a string.');
    }
  }

  // Check for sensitive fields that shouldn't be in import
  const sensitiveFields = ['id', '_id', 'orgId', 'organization_id', 'createdAt', 'updatedAt'];
  sensitiveFields.forEach(field => {
    if (configObj[field]) {
      warnings.push(`Sensitive field '${field}' will be ignored during import.`);
    }
  });

  // Check for organization-specific fields
  const model = configObj.model as Record<string, unknown>;
  if (model?.toolIds && Array.isArray(model.toolIds) && model.toolIds.length > 0) {
    warnings.push('Tool IDs will be ignored during import as they are organization-specific. Please select tools manually after import.');
  }
  if (model?.knowledgeBaseId && typeof model.knowledgeBaseId === 'string') {
    warnings.push('Knowledge Base ID will be ignored during import as it is organization-specific. Please select a knowledge base manually after import.');
  }
  // Check for any extra model fields that will be removed
  if (model) {
    const allowedModelFields = ['messages', 'temperature', 'maxTokens', 'numFastTurns', 'semanticCachingEnabled', 'emotionRecognitionEnabled', 'toolIds', 'knowledgeBaseId'];
    const extraFields = Object.keys(model).filter(key => !allowedModelFields.includes(key));
    if (extraFields.length > 0) {
      warnings.push(`Extra model fields will be ignored: ${extraFields.join(', ')}`);
    }
  }

  // Check for server config (completely excluded from import)
  if (configObj.server) {
    warnings.push('Server configuration will be ignored during import as it\'s not part of the standard assistant creation API.');
  }

  // Check for extra fields that aren't in default config
  const startSpeaking = configObj.startSpeakingPlan as Record<string, unknown>;
  if (startSpeaking?.smartEndpointingPlan) {
    warnings.push('smartEndpointingPlan will be ignored during import as it\'s not in the default configuration.');
  }


  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Process imported configuration and merge with defaults
 */
export const processImportedConfig = (importedConfig: unknown): Omit<Assistant, 'name'> => {
  // Validate first
  const validation = validateImportedConfig(importedConfig);
  if (!validation.isValid) {
    throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
  }

  // Remove sensitive fields - ensure it's an object first
  if (!importedConfig || typeof importedConfig !== 'object') {
    throw new Error('Invalid configuration format');
  }
  
  const cleanConfig = { ...importedConfig } as Record<string, unknown>;
  EXCLUDED_FIELDS.forEach(field => {
    delete cleanConfig[field];
  });

  // Remove organization-specific tool IDs and knowledge base ID
  if (cleanConfig.model) {
    const model = cleanConfig.model as Record<string, unknown>;
    if (model?.toolIds) {
      delete model.toolIds;
    }
    if (model?.knowledgeBaseId) {
      delete model.knowledgeBaseId;
    }
    // Remove any extra fields that might be present but not in schema
    const allowedModelFields = ['messages', 'temperature', 'maxTokens', 'numFastTurns', 'semanticCachingEnabled', 'emotionRecognitionEnabled'];
    Object.keys(model).forEach(key => {
      if (!allowedModelFields.includes(key) && key !== 'toolIds' && key !== 'knowledgeBaseId') {
        delete model[key];
      }
    });
  }

  // Remove server URL and secret if present for security
  const server = cleanConfig.server as Record<string, unknown>;
  if (server?.url) {
    delete server.url;
  }
  if (server?.secret) {
    delete server.secret;
  }

  // Create model with only allowed fields (whitelist approach)
  const modelConfig = cleanConfig.model as Record<string, unknown> || {};
  const allowedModelFields = ['messages', 'temperature', 'maxTokens', 'numFastTurns', 'semanticCachingEnabled', 'emotionRecognitionEnabled'];
  const cleanModelConfig: Record<string, unknown> = {};
  
  // Only copy allowed fields
  allowedModelFields.forEach(field => {
    if (modelConfig[field] !== undefined) {
      cleanModelConfig[field] = modelConfig[field];
    }
  });
  

  // Deep merge with defaults to ensure all required nested fields are present
  const processedConfig: Omit<Assistant, 'name'> = {
    ...defaultAssistantConfig,
    ...cleanConfig,
    // Deep merge nested objects to preserve all required fields
    model: {
      ...defaultAssistantConfig.model,
      ...cleanModelConfig
      // toolIds will come from defaultAssistantConfig.model (empty array)
      // knowledgeBaseId will not exist (matching default config)
    },
    voice: {
      ...defaultAssistantConfig.voice,
      ...(cleanConfig.voice as Record<string, unknown>),
      chunkPlan: {
        ...defaultAssistantConfig.voice?.chunkPlan,
        ...(cleanConfig.voice as Record<string, unknown>)?.chunkPlan as Record<string, unknown>,
        formatPlan: {
          ...defaultAssistantConfig.voice?.chunkPlan?.formatPlan,
          ...((cleanConfig.voice as Record<string, unknown>)?.chunkPlan as Record<string, unknown>)?.formatPlan as Record<string, unknown>
        }
      }
    },
    analysisPlan: {
      ...defaultAssistantConfig.analysisPlan,
      ...(cleanConfig.analysisPlan as Record<string, unknown>),
      structuredDataSchema: {
        ...defaultAssistantConfig.analysisPlan?.structuredDataSchema,
        ...(cleanConfig.analysisPlan as Record<string, unknown>)?.structuredDataSchema as Record<string, unknown>
      }
    },
    artifactPlan: {
      ...defaultAssistantConfig.artifactPlan,
      ...(cleanConfig.artifactPlan as Record<string, unknown>),
      transcriptPlan: {
        ...defaultAssistantConfig.artifactPlan?.transcriptPlan,
        ...(cleanConfig.artifactPlan as Record<string, unknown>)?.transcriptPlan as Record<string, unknown>
      }
    },
    messagePlan: {
      ...defaultAssistantConfig.messagePlan,
      ...(cleanConfig.messagePlan as Record<string, unknown>)
    },
    startSpeakingPlan: (() => {
      const startSpeakingConfig = cleanConfig.startSpeakingPlan as Record<string, unknown> || {};
      // Remove smartEndpointingPlan if present (not in default config)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { smartEndpointingPlan, ...cleanStartSpeakingConfig } = startSpeakingConfig;
      return {
        ...defaultAssistantConfig.startSpeakingPlan,
        ...cleanStartSpeakingConfig,
        transcriptionEndpointingPlan: {
          ...defaultAssistantConfig.startSpeakingPlan?.transcriptionEndpointingPlan,
          ...(startSpeakingConfig?.transcriptionEndpointingPlan as Record<string, unknown> || {})
        }
      };
    })(),
    stopSpeakingPlan: {
      ...defaultAssistantConfig.stopSpeakingPlan,
      ...(cleanConfig.stopSpeakingPlan as Record<string, unknown>)
    },
    monitorPlan: {
      ...defaultAssistantConfig.monitorPlan,
      ...(cleanConfig.monitorPlan as Record<string, unknown>)
    },
    transcriber: {
      ...defaultAssistantConfig.transcriber,
      ...(cleanConfig.transcriber as Record<string, unknown>)
    },
    voicemailDetection: {
      ...defaultAssistantConfig.voicemailDetection,
      ...(cleanConfig.voicemailDetection as Record<string, unknown>)
    },
    transportConfigurations: (cleanConfig.transportConfigurations as Array<unknown>) || defaultAssistantConfig.transportConfigurations,
    // Never include server field - it's not part of standard assistant creation API
    // Server configurations are handled separately and not included in assistant payload
  } as Omit<Assistant, 'name'>;


  return processedConfig;
};

/**
 * Read and parse JSON file
 */
export const readJsonFile = (file: File): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const json = JSON.parse(content);
        resolve(json);
      } catch {
        reject(new Error('Invalid JSON file format'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};
